import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import { Link } from "react-router-dom";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import Select from "react-select";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  PointElement,
  Tooltip,
  Legend,
  BarElement,
} from "chart.js";

import { formatCurrencyCOP } from "../../utils/format";
import LoadingOverlay from "../../components/LoadingOverlay";

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
  BarElement,
  ChartDataLabels,
);

function InvoicesDashboard() {
  const [loading, setLoading] = useState(true);

  const [summary, setSummary] = useState({
    totalInvoices: 0,
    totalAmount: 0,
    totalRetention: 0,
    totalReteIca: 0,
    totalPaid: 0,
    totalPending: 0,
  });

  const [monthly, setMonthly] = useState([]);
  const [statusSummary, setStatusSummary] = useState([]);
  const [topSuppliers, setTopSuppliers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [portfolio, setPortfolio] = useState(null);

  const [filters, setFilters] = useState({
    supplier_id: "",
    status: "",
    from: "",
    to: "",
  });

  useEffect(() => {
    fetchSuppliers();
    setDefaultMonthFilters();
    // eslint-disable-next-line
  }, []);

  const getDefaultFilters = () => {
    const today = new Date();

    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
      .toISOString()
      .split("T")[0];

    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      .toISOString()
      .split("T")[0];

    return {
      supplier_id: "",
      status: "",
      from: firstDay,
      to: lastDay,
    };
  };

  const setDefaultMonthFilters = () => {
    const defaultFilters = getDefaultFilters();
    setFilters(defaultFilters);
    fetchFilteredDashboard(defaultFilters);
  };

  const fetchSuppliers = async () => {
    try {
      const res = await api.get("/suppliers");
      setSuppliers(res.data || []);
    } catch (e) {
      console.error("No se pudieron cargar proveedores", e);
    }
  };

  const fetchFilteredDashboard = async (useFilters = {}) => {
    setLoading(true);

    try {
      const params = { ...filters, ...useFilters };

      const res = await api.get("/invoices/dashboard/filtered", { params });

      const { summary, monthly, status, topSuppliers } = res.data;

      console.log("MONTHLY RAW 👉", monthly);
      setSummary(
        summary || {
          totalInvoices: 0,
          totalAmount: 0,
          totalPaid: 0,
          totalBalance: 0,
        },
      );

      setMonthly(monthly || []);
      setStatusSummary(status || []);
      setTopSuppliers(topSuppliers || []);
    } catch (err) {
      console.error("Error cargando dashboard filtrado", err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    fetchFilteredDashboard(filters);
  };

  const clearFilters = () => {
    const defaultFilters = getDefaultFilters();

    setFilters(defaultFilters);
    setPortfolio(null); // ✅ elimina el bloque de cartera
    fetchFilteredDashboard(defaultFilters);
  };

  const months = monthly.map((m) => m.month);
  const totalPerMonth = monthly.map((m) => Number(m.totalAmount || 0));
  const paidPerMonth = monthly.map((m) => Number(m.paid || 0));
  const pendingPerMonth = monthly.map((m) => Number(m.pending || 0));

  const retentionPerMonth = monthly.map((m) =>
    Number(m.retention || 0)
  );

  const reteicaPerMonth = monthly.map((m) =>
    Number(m.rete_ica || 0)
  );


  const stackedData = {
    labels: months,
    datasets: [
      {
        label: "Pagado",
        data: paidPerMonth,
        stack: "stack1",
        backgroundColor: "rgba(16,185,129,0.8)",
      },
      {
        label: "Pendiente",
        data: pendingPerMonth,
        stack: "stack1",
        backgroundColor: "rgba(239,68,68,0.8)",
      },
    ],
  };

  const stackedOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" },

      datalabels: {
        color: "#fff",
        font: {
          weight: "bold",
          size: 12,
        },
        formatter: (value, context) => {
          const index = context.dataIndex;

          const paid = paidPerMonth[index] || 0;
          const pending = pendingPerMonth[index] || 0;
          const total = paid + pending;

          if (!total) return "";

          const percentage = (value / total) * 100;

          // Evita mostrar porcentajes muy pequeños
          return percentage >= 5 ? `${percentage.toFixed(1)}%` : "";
        },
      },
    },
    scales: {
      x: { stacked: true },
      y: {
        stacked: true,
        ticks: {
          callback: (v) => `$${Number(v).toLocaleString("es-CO")}`,
        },
      },
    },
  };

  const lineData = {
    labels: months,
    datasets: [
      {
        label: "Facturado",
        data: totalPerMonth,
        borderColor: "rgb(37,99,235)",
        tension: 0.3,
        fill: false,
      },
      {
        label: "Pagado",
        data: paidPerMonth,
        borderColor: "rgb(16,185,129)",
        tension: 0.3,
        fill: false,
      },
    ],
  };

  const statusData = {
    labels: statusSummary.map((s) => s.status),
    datasets: [
      {
        data: statusSummary.map((s) => s.count),
        backgroundColor: ["#3b82f6", "#10b981", "#f97316", "#ef4444"],
      },
    ],
  };

  const loadSupplierPortfolio = async () => {
    const res = await api.get(
      `/invoices/dashboard/supplier-portfolio?supplier_id=${filters.supplier_id}`,
    );
    setPortfolio(res.data);
  };

  const suppliersBarData = {
    labels: topSuppliers.map((s) => s.supplier || `ID: ${s.supplier_id}`),
    datasets: [
      {
        label: "Saldo pendiente",
        data: topSuppliers.map((s) => s.balance),
        backgroundColor: topSuppliers.map(
          (_, i) =>
            `rgba(${40 + i * 20}, ${120 + i * 10}, ${200 - i * 15}, 0.8)`,
        ),
      },
    ],
  };

  const smallLineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },

      // 👇 APAGA LOS NÚMEROS SOBRE LOS PUNTOS
      datalabels: {
        display: false,
      },

      tooltip: {
        callbacks: {
          label: (ctx) =>
            `$${ctx.raw.toLocaleString("es-CO")}`,
        },
      },
    },
    scales: {
      x: {
        ticks: { font: { size: 11 } },
      },
      y: {
        ticks: {
          callback: (v) =>
            `$${Number(v).toLocaleString("es-CO")}`,
          font: { size: 11 },
        },
      },
    },
  };


  const retentionData = {
    labels: months,
    datasets: [
      {
        label: "Retención",
        data: retentionPerMonth,
        borderColor: "#16a34a",
        backgroundColor: "#16a34a",
        tension: 0.4,
        pointRadius: 4,
        fill: false,
      },
    ],
  };

  const reteicaData = {
    labels: months,
    datasets: [
      {
        label: "ReteICA",
        data: reteicaPerMonth,
        borderColor: "#2563eb",
        backgroundColor: "#2563eb",
        tension: 0.4,
        pointRadius: 4,
        fill: false,
      },
    ],
  };

  const handleFilterOldestDebt = async () => {
    try {
      const response = await api.get("/invoices/dashboard/debt-range");
      const { from } = response.data;

      // Obtener fecha local en formato YYYY-MM-DD sin problemas de zona horaria
      const now = new Date();
      const today = now.getFullYear() + '-' +
        String(now.getMonth() + 1).padStart(2, '0') + '-' +
        String(now.getDate()).padStart(2, '0');

      const newFilters = {
        ...filters,
        from: from,
        to: today,
      };
      setFilters(newFilters);
      fetchFilteredDashboard(newFilters);

    } catch (error) {
      console.error("Error al setear rango de deuda", error);
    }
  };
  if (loading) {
    return <p className="text-gray-500">Cargando dashboard...</p>;
  }

  const dashboardSupplierOptions = [
    { value: "", label: "-Seleccionar Proveedor" },
    ...suppliers.map((s) => ({
      value: s.id,
      label: s.name,
    })),
  ];

  return (
    <div className="bg-white p-6 rounded-xl shadow-md">
      {/* ENCABEZADO CON TITULO Y CONTADOR */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 border-b pb-4 gap-4">
        <h2 className="text-3xl font-bold text-slate-800">Dashboard de Facturas</h2>
        <div className="bg-blue-50 px-6 py-2 rounded-full border border-blue-200 flex items-center shadow-sm">
          <span className="text-sm font-bold text-blue-600 uppercase tracking-wider mr-3">Número de Facturas:</span>
          <span className="text-3xl font-black text-blue-900 leading-none">{summary.totalInvoices}</span>
        </div>
      </div>

      {/* BLOQUES RESUMEN - 2 FILAS DE 4 */}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">

        {/* FILA 1: RESULTADOS FINANCIEROS CLAVE */}

        {/* 1. CUENTAS PAGADAS - Azul Slate Moderno */}
        <div className="bg-slate-50/50 p-5 rounded-xl border border-slate-200 border-l-4 border-l-slate-500 shadow-sm text-center">
          <h3 className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1">Cuentas Pagadas</h3>
          <div className="text-xl font-bold text-slate-800">
            {formatCurrencyCOP(summary.totalCashOut)}
          </div>
        </div>


        {/* 3. TOTAL BRUTO - Neutro Elegante */}
        <div className="bg-gray-50/50 p-5 rounded-xl border border-gray-200 border-l-4 border-l-gray-400 shadow-sm text-center">
          <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-500 mb-1">Total Bruto</h3>
          <div className="text-xl font-bold text-gray-800">
            {formatCurrencyCOP(summary.totalAmount)}
          </div>
        </div>

        {/* 4. DEDUCCIONES - Púrpura Desaturado */}
        <div className="bg-purple-50/30 p-5 rounded-xl border border-purple-200 border-l-4 border-l-purple-500 shadow-sm text-center">
          <h3 className="text-[10px] uppercase font-bold tracking-wider text-purple-600 mb-1">Glosas / Descuentos</h3>
          <div className="text-xl font-bold text-purple-900">
            {formatCurrencyCOP(summary.totalDeductions)}
          </div>
        </div>

        {/* FILA 2: IMPUESTOS Y CARTERA */}

        {/* 5. RETENCIONES - Naranja Pastel */}
        <div className="bg-orange-50/30 p-5 rounded-xl border border-orange-200 border-l-4 border-l-orange-500 shadow-sm text-center">
          <h3 className="text-[10px] uppercase font-bold tracking-wider text-orange-600 mb-1">Retenciones</h3>
          <div className="text-xl font-bold text-orange-900">
            {formatCurrencyCOP(summary.totalRetention)}
          </div>
        </div>

        {/* 6. RETE ICA - Ámbar Soft */}
        <div className="bg-amber-50/30 p-5 rounded-xl border border-amber-200 border-l-4 border-l-amber-500 shadow-sm text-center">
          <h3 className="text-[10px] uppercase font-bold tracking-wider text-amber-600 mb-1">Rete ICA</h3>
          <div className="text-xl font-bold text-amber-900">
            {formatCurrencyCOP(summary.totalReteIca)}
          </div>
        </div>

        {/* 2. VALOR NETO REAL - Unificado a estilo lateral */}
        <div className="bg-indigo-50/30 p-5 rounded-xl border border-indigo-200 border-l-4 border-l-indigo-500 shadow-sm text-center">
          <h3 className="text-[10px] uppercase font-bold tracking-wider text-indigo-600 mb-1">Valor Neto</h3>
          <div className="text-xl font-bold text-indigo-900">
            {formatCurrencyCOP(summary.totalNeto)}
          </div>
        </div>

        {/* 7. ABONOS A FACTURAS - Verde Esmeralda Soft */}
        <div className="bg-emerald-50/30 p-5 rounded-xl border border-emerald-200 border-l-4 border-l-emerald-500 shadow-sm text-center">
          <h3 className="text-[10px] uppercase font-bold tracking-wider text-emerald-700 mb-1">Abonos a Facturas</h3>
          <div className="text-xl font-bold text-emerald-900">
            {formatCurrencyCOP(summary.totalPaid)}
          </div>
        </div>

        {/* 8. SALDO PENDIENTE - Rojo Coral Soft */}
        <div className="bg-rose-50/30 p-5 rounded-xl border border-rose-200 border-l-4 border-l-rose-500 shadow-sm text-center">
          <h3 className="text-[10px] uppercase font-bold tracking-wider text-rose-700 mb-1">Saldo Pendiente</h3>
          <div className="text-xl font-bold text-rose-900">
            {formatCurrencyCOP(summary.totalPending)}
          </div>
        </div>

      </div>


      {/* FILTROS */}
      <div className="flex gap-2 items-center">
        <button
          className={`p-2 rounded border ${filters.supplier_id
            ? "bg-blue-600 text-white"
            : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          title="Ver cartera del proveedor"
          disabled={!filters.supplier_id}
          onClick={loadSupplierPortfolio}
        >
          📄
        </button>

        <div className="w-full">
          <Select
            options={dashboardSupplierOptions}
            placeholder="Buscar proveedor..."
            // Buscamos el objeto que coincida con el ID dentro del objeto filters
            value={dashboardSupplierOptions.find(o => o.value === filters.supplier_id) || dashboardSupplierOptions}
            onChange={(option) => {
              setFilters({ ...filters, supplier_id: option?.value || "" });
              setPortfolio(null); // Mantenemos tu lógica de limpiar cartera
            }}
            // Estilos para que se vea limpio y ocupe todo el ancho
            className="w-full"
            classNamePrefix="react-select"
            isSearchable={true}
            // Opcional: permite borrar la selección con una X
            isClearable={true}
          />
        </div>

        <select
          className="border p-2 rounded"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">Estado</option>
          <option value="pending">Pendiente</option>
          <option value="partial">Parcial</option>
          <option value="paid">Pagada</option>
        </select>

        <button
          type="button"
          className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded text-sm whitespace-nowrap transition-colors"
          onClick={handleFilterOldestDebt}
          title="Filtrar desde la factura más antigua pendiente"
        >
          Cartera
        </button>

        <input
          type="date"
          className="border p-2 rounded"
          value={filters.from}
          onChange={(e) => setFilters({ ...filters, from: e.target.value })}
        />

        <input
          type="date"
          className="border p-2 rounded"
          value={filters.to}
          onChange={(e) => setFilters({ ...filters, to: e.target.value })}
        />

        <div className="flex gap-2">
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded"
            onClick={applyFilters}
          >
            Filtrar
          </button>

          <button
            className="bg-gray-200 px-4 py-2 rounded"
            onClick={clearFilters}
          >
            Limpiar
          </button>
        </div>
      </div>

      {portfolio && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <div className="bg-blue-100 p-4 rounded text-center">
            <h4 className="font-semibold">Facturas Pendientes</h4>
            <div className="text-2xl font-bold">{portfolio.totalInvoices}</div>
          </div>

          <div className="bg-red-100 p-4 rounded text-center">
            <h4 className="font-semibold">Valor Adeudado</h4>
            <div className="text-2xl font-bold">
              {formatCurrencyCOP(portfolio.totalBalance)}
            </div>
          </div>
        </div>
      )}

      {/* GRÁFICOS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-gray-50 p-4 rounded">
          <h3 className="font-bold mb-2">Facturado vs Pagado (mensual)</h3>
          <Bar data={stackedData} options={stackedOptions} />
        </div>

        <div className="bg-gray-50 p-4 rounded">
          <h3 className="font-bold mb-2">Tendencia (Facturado / Pagado)</h3>
          <Line
            data={lineData}
            options={{
              plugins: {
                datalabels: {
                  display: false,
                },
              },
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 p-4 rounded h-56">
            <h3 className="font-bold mb-2 text-sm">
              Retención mensual
            </h3>
            <Line data={retentionData} options={smallLineOptions} />
          </div>

          <div className="bg-gray-50 p-4 rounded h-56">
            <h3 className="font-bold mb-2 text-sm">
              ReteICA mensual
            </h3>
            <Line data={reteicaData} options={smallLineOptions} />
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded">
          <h3 className="font-bold mb-2">Top proveedores - saldo pendiente</h3>
          <Bar
            data={suppliersBarData}
            options={{
              indexAxis: "y",
              plugins: {
                legend: { display: false },
                datalabels: {
                  display: false,
                },
              },
            }}
          />
        </div>

        <div className="bg-gray-50 p-4 rounded h-96 lg:col-span-2">
          {" "}
          <h3 className="font-bold mb-2">Estado de facturas</h3>
          <Doughnut
            data={statusData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              radius: "100%", // 👈 tamaño total
              cutout: "40%", // 👈 grosor de la dona
              plugins: {
                legend: {
                  position: "bottom",
                },
                datalabels: {
                  display: false,
                },
              },
            }}
          />
        </div>

      </div>
    </div>
  );
}

export default InvoicesDashboard;
